import { CircleIcon, Text, Flex, LabelColor } from '@shared/components';

type DifficultyLevel = 'easy' | 'medium' | 'hard';

const DIFFICULTY_RANK: Record<DifficultyLevel, number> = {
  easy: 1,
  medium: 2,
  hard: 3,
};

export function getDifficultyLevelDiff(
  currentDifficultyLevel?: DifficultyLevel,
  targetDifficultyLevel?: DifficultyLevel,
): number {
  const calculateDifficultyRank = (level: DifficultyLevel | undefined): number =>
    level ? DIFFICULTY_RANK[level] : 0;

  return Math.abs(
    calculateDifficultyRank(currentDifficultyLevel) -
      calculateDifficultyRank(targetDifficultyLevel),
  );
}

function checkDifficultyLevelIndicative(
  value: number | null,
  pBufferHardThreshold: number | null,
  pBufferMediumThreshold: number | null,
  pBufferEasyThreshold: number | null,
): DifficultyLevel | undefined {
  if (
    value == null ||
    pBufferHardThreshold == null ||
    pBufferMediumThreshold == null ||
    pBufferEasyThreshold == null
  ) {
    return;
  }
  const valueFixed = Number(value.toFixed(2));

  switch (true) {
    case valueFixed <= pBufferHardThreshold:
      return 'hard';
    case valueFixed > pBufferHardThreshold && valueFixed <= pBufferMediumThreshold:
      return 'medium';
    case valueFixed > pBufferMediumThreshold && valueFixed <= pBufferEasyThreshold:
      return 'easy';
    default:
      return;
  }
}

export function renderDifficultyLevelCell(
  value: number | null,
  pBufferHardThreshold: number | null,
  pBufferMediumThreshold: number | null,
  pBufferEasyThreshold: number | null,
) {
  const formatNumberToTwoDecimal = (value: number) => {
    return value.toFixed(2);
  };
  if (
    value == null ||
    pBufferHardThreshold == null ||
    pBufferMediumThreshold == null ||
    pBufferEasyThreshold == null
  ) {
    return '-';
  }
  const indicative = checkDifficultyLevelIndicative(
    value,
    pBufferHardThreshold,
    pBufferMediumThreshold,
    pBufferEasyThreshold,
  );
  if (!indicative) {
    return '-';
  }

  switch (indicative) {
    case 'hard':
      return (
        <Flex sx={{ alignItems: 'center' }}>
          <CircleIcon
            sx={(theme) => ({
              width: '10px',
              height: '10px',
              mr: theme.spacing(2),
              color: theme.palette.error.main,
            })}
          />
          <Text>{formatNumberToTwoDecimal(value)} (ยาก)</Text>
        </Flex>
      );
    case 'medium':
      return (
        <Flex sx={{ alignItems: 'center' }}>
          <CircleIcon
            sx={(theme) => ({
              width: '10px',
              height: '10px',
              mr: theme.spacing(2),
              color: theme.palette.warning.main,
            })}
          />
          <Text>{formatNumberToTwoDecimal(value)} (กลาง)</Text>
        </Flex>
      );
    case 'easy':
      return (
        <Flex sx={{ alignItems: 'center' }}>
          <CircleIcon
            sx={(theme) => ({
              width: '10px',
              height: '10px',
              mr: theme.spacing(2),
              color: theme.palette.success.main,
            })}
          />
          <Text>{formatNumberToTwoDecimal(value)} (ง่าย)</Text>
        </Flex>
      );
    default:
      return '-';
  }
}

export interface RenderIndicativeCellParams {
  pUse: number | null;
  pBuffer: number | null;
  pBufferHardThreshold: number | null;
  pBufferMediumThreshold: number | null;
  pBufferEasyThreshold: number | null;
  pBufferSettingHardThreshold: number | null;
  pBufferSettingMediumThreshold: number | null;
  pBufferSettingEasyThreshold: number | null;
}

export function renderIndicativeCell({
  pUse,
  pBuffer,
  pBufferHardThreshold,
  pBufferMediumThreshold,
  pBufferEasyThreshold,
  pBufferSettingHardThreshold,
  pBufferSettingMediumThreshold,
  pBufferSettingEasyThreshold,
}: RenderIndicativeCellParams) {
  if (pUse == null || pBuffer == null) {
    return '-';
  }

  const bufferIndicative = checkDifficultyLevelIndicative(
    pBuffer,
    pBufferSettingHardThreshold,
    pBufferSettingMediumThreshold,
    pBufferSettingEasyThreshold,
  );

  const pUseIndicative = checkDifficultyLevelIndicative(
    pUse,
    pBufferHardThreshold,
    pBufferMediumThreshold,
    pBufferEasyThreshold,
  );

  const diff = getDifficultyLevelDiff(bufferIndicative, pUseIndicative);

  if (diff === 0) {
    return {
      label: 'No',
      color: LabelColor.GREY,
      diff: 0,
    };
  }

  return {
    label: 'Yes',
    color: diff === 1 ? LabelColor.WARNING : LabelColor.ERROR,
    diff,
  };
}
